import React, { useState } from "react";
import { Phone, MessageCircle, MapPin, Mail, Instagram, Youtube, Globe, Clock } from "lucide-react";
import { useClinic } from "../context/ClinicContext";
import { Tx } from "../context/AutoTranslateContext";

const Contact = () => {
  const { clinic } = useClinic();
  const [copied, setCopied] = useState("");

  if (!clinic) {
    return <div className="max-w-5xl mx-auto py-20 px-4 text-center text-mute">Loading…</div>;
  }

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(""), 1500);
  };

  const mapUrl = `https://www.google.com/maps?q=${encodeURIComponent(clinic.address.maps_query)}&output=embed`;
  const waUrl = `https://wa.me/${clinic.whatsapp}?text=${encodeURIComponent("As-salamu alaykum, I'd like to book an appointment.")}`;

  return (
    <div data-testid="contact-page" className="max-w-7xl mx-auto px-4 sm:px-8 py-12 sm:py-20">
      <div className="mb-12 max-w-3xl">
        <p className="text-xs uppercase tracking-[0.25em] text-gold mb-3"><Tx>Visit · Book · Reach Out</Tx></p>
        <h1 data-testid="contact-title" className="font-heading text-4xl sm:text-5xl lg:text-6xl text-forest font-bold tracking-tight mb-5">
          <Tx>The clinic, the people, the place.</Tx>
        </h1>
        <p className="text-mute leading-relaxed text-lg">
          {clinic.name} — {clinic.tagline}. <Tx>Walk-in, call, or book your Hijama, Ruqyah, or herbal consultation with our qualified, well-experienced practitioners.</Tx>
        </p>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <a
            data-testid="contact-call"
            href={`tel:${clinic.phone_landline}`}
            className="clay-surface rounded-3xl p-6 flex items-center gap-5 lift-on-hover"
          >
            <div className="w-12 h-12 rounded-2xl bg-forest text-sand flex items-center justify-center"><Phone className="w-5 h-5" /></div>
            <div>
              <p className="text-xs uppercase tracking-widest text-gold mb-1"><Tx>Phone</Tx></p>
              <p className="font-heading text-xl text-forest font-semibold"><bdi dir="ltr">{clinic.phone_landline_display}</bdi></p>
            </div>
          </a>

          <a
            data-testid="contact-whatsapp"
            href={waUrl}
            target="_blank"
            rel="noreferrer"
            className="clay-surface rounded-3xl p-6 flex items-center gap-5 lift-on-hover"
          >
            <div className="w-12 h-12 rounded-2xl bg-gold text-sand flex items-center justify-center"><MessageCircle className="w-5 h-5" /></div>
            <div>
              <p className="text-xs uppercase tracking-widest text-gold mb-1"><Tx>Mobile / WhatsApp</Tx></p>
              <p className="font-heading text-xl text-forest font-semibold"><bdi dir="ltr">{clinic.phone_mobile_display}</bdi></p>
              <p className="text-xs text-mute mt-1"><Tx>Tap to message on WhatsApp</Tx></p>
            </div>
          </a>

          <a
            data-testid="contact-email"
            href={`mailto:${clinic.email}`}
            className="clay-surface rounded-3xl p-6 flex items-center gap-5 lift-on-hover"
          >
            <div className="w-12 h-12 rounded-2xl bg-forest/10 text-forest flex items-center justify-center"><Mail className="w-5 h-5" /></div>
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-widest text-gold mb-1"><Tx>Email</Tx></p>
              <p className="font-heading text-base text-forest font-semibold break-all">{clinic.email}</p>
            </div>
          </a>

          <a
            data-testid="contact-website"
            href={clinic.website}
            target="_blank"
            rel="noreferrer"
            className="clay-surface rounded-3xl p-6 flex items-center gap-5 lift-on-hover"
          >
            <div className="w-12 h-12 rounded-2xl bg-forest/10 text-forest flex items-center justify-center"><Globe className="w-5 h-5" /></div>
            <div>
              <p className="text-xs uppercase tracking-widest text-gold mb-1"><Tx>Website</Tx></p>
              <p className="font-heading text-base text-forest font-semibold">islamiccureandcare.co.uk</p>
            </div>
          </a>

          <div className="flex gap-2">
            <a
              href={clinic.social.instagram}
              target="_blank"
              rel="noreferrer"
              data-testid="social-instagram"
              className="flex-1 clay-surface rounded-2xl p-4 flex items-center justify-center gap-2 text-forest hover:bg-cream transition"
            >
              <Instagram className="w-4 h-4" />
              <span className="text-sm">Instagram</span>
            </a>
            <a
              href={clinic.social.youtube}
              target="_blank"
              rel="noreferrer"
              data-testid="social-youtube"
              className="flex-1 clay-surface rounded-2xl p-4 flex items-center justify-center gap-2 text-forest hover:bg-cream transition"
            >
              <Youtube className="w-4 h-4" />
              <span className="text-sm">YouTube</span>
            </a>
          </div>
        </div>

        <div className="lg:col-span-3 space-y-4">
          <div className="clay-surface rounded-3xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <MapPin className="w-5 h-5 text-gold" />
              <h2 className="font-heading text-xl text-forest font-semibold"><Tx>Find us</Tx></h2>
            </div>
            <p className="text-ink leading-relaxed mb-2"><bdi dir="ltr">{clinic.address.line1}</bdi></p>
            <p className="text-mute mb-1"><bdi dir="ltr">{clinic.address.city}, {clinic.address.postcode}</bdi></p>
            <p className="text-mute mb-4"><bdi dir="ltr">{clinic.address.country}</bdi></p>
            <div className="rounded-2xl overflow-hidden border border-forest/10">
              <iframe
                data-testid="contact-map"
                src={mapUrl}
                title="Clinic location"
                width="100%"
                height="320"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          <div className="clay-surface-soft rounded-3xl p-6 flex items-start gap-4">
            <Clock className="w-5 h-5 text-gold flex-shrink-0 mt-1" />
            <div>
              <p className="text-xs uppercase tracking-widest text-gold mb-1"><Tx>Appointments</Tx></p>
              <p className="text-sm text-mute leading-relaxed">
                <Tx>Hijama, Ruqyah, and consultation sessions are available by appointment. Please call or WhatsApp to book your preferred time slot. Walk-ins are welcomed during clinic hours — call ahead to confirm a practitioner is available.</Tx>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
